export interface ConnectAnalyticsResponse {
  timeframe?: "7d" | "30d" | "quarter" | "ytd";
  executive_snapshot?: {
    period: {
      start_at: string;
      end_at: string;
      previous_start_at: string;
      previous_end_at: string;
    };
    resident_interactions: {
      value: number;
      previous_value: number;
      delta: number;
      delta_percentage: number | null;
    };
    top_topic: {
      name: string;
      count: number;
      share_percentage: number;
    } | null;
    daily_interaction_velocity: {
      value: number;
      unit: string;
    };
  };
  questions_over_time?: { month: string; questions: number }[];
  popular_topics: { topic: string; chats: number }[];
  questions_per_month: { month: string; questions: number }[];
  recent_questions: {
    id: number;
    content: string;
    asked_at: string;
    answer: string | null;
    topic: string | null;
    source: string | null;
    sources: string[];
  }[];
  citizen_insights: { summary: string | null; generated_at: string | null };
  customer_complaints: {
    id: number;
    complaint: string;
    submitted_at: string;
    contact_name: string | null;
    contact_email: string | null;
    photo_count: number;
    photos: {
      id: number;
      filename: string;
      content_type: string;
      byte_size: number;
      url: string;
    }[];
    address_data?: {
      address?: string;
      formatted_address?: string;
      city?: string;
      source?: string;
      [key: string]: string | undefined;
    };
    contact_phone: string | null;
    incident_latitude: number | null;
    incident_longitude: number | null;
    incident_location_description: string | null;
  }[];
}

export interface ConnectAnalyticsQuestionsResponse {
  questions: ConnectAnalyticsResponse["recent_questions"];
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  next_page: number | null;
}

export interface ConnectAnalyticsComplaintsResponse {
  complaints: ConnectAnalyticsResponse["customer_complaints"];
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  next_page: number | null;
}

async function buildErrorMessage(response: Response): Promise<string> {
  try {
    const data = await response.json();
    if (typeof data?.error === "string" && data.error.length > 0) {
      return data.error;
    }
    if (typeof data?.message === "string" && data.message.length > 0) {
      return data.message;
    }
  } catch {
    // Fall through to status text.
  }
  return response.statusText || "Request failed";
}

export async function fetchConnectAnalytics(
  municipalityId: number,
  options: { timeframe?: "7d" | "30d" | "quarter" | "ytd" } = {},
): Promise<ConnectAnalyticsResponse> {
  const query = new URLSearchParams({ municipality_id: String(municipalityId) });
  if (options.timeframe) {
    query.set("timeframe", options.timeframe);
  }
  const response = await fetch(`/api/v1/connect_analytics?${query.toString()}`);

  if (!response.ok) {
    const message = await buildErrorMessage(response);
    throw new Error(message);
  }

  return response.json();
}

export async function fetchConnectAnalyticsQuestion(
  municipalityId: number,
  questionId: number,
  signal?: AbortSignal,
): Promise<ConnectAnalyticsResponse["recent_questions"][number]> {
  const response = await fetch(`/api/v1/connect_analytics/questions/${questionId}?municipality_id=${municipalityId}`, { signal });

  if (!response.ok) {
    const message = await buildErrorMessage(response);
    throw new Error(message);
  }

  return response.json();
}

export async function fetchConnectAnalyticsQuestions(
  municipalityId: number,
  options: {
    signal?: AbortSignal;
    page?: number;
    q?: string;
    topic?: string;
  } = {},
): Promise<ConnectAnalyticsQuestionsResponse> {
  const query = new URLSearchParams({ municipality_id: String(municipalityId) });
  if (typeof options.page === "number") {
    query.set("page", String(options.page));
  }
  if (options.q && options.q.trim().length > 0) {
    query.set("q", options.q.trim());
  }
  if (options.topic && options.topic.trim().length > 0) {
    query.set("topic", options.topic.trim());
  }
  const response = await fetch(`/api/v1/connect_analytics/questions?${query.toString()}`, { signal: options.signal });

  if (!response.ok) {
    const message = await buildErrorMessage(response);
    throw new Error(message);
  }

  return response.json();
}

export async function fetchConnectAnalyticsComplaints(
  municipalityId: number,
  options: {
    signal?: AbortSignal;
    page?: number;
    timeframe?: "7d" | "30d" | "quarter" | "ytd";
  } = {},
): Promise<ConnectAnalyticsComplaintsResponse> {
  const query = new URLSearchParams({ municipality_id: String(municipalityId) });
  if (typeof options.page === "number") {
    query.set("page", String(options.page));
  }
  if (options.timeframe) {
    query.set("timeframe", options.timeframe);
  }
  const response = await fetch(`/api/v1/connect_analytics/complaints?${query.toString()}`, { signal: options.signal });

  if (!response.ok) {
    const message = await buildErrorMessage(response);
    throw new Error(message);
  }

  return response.json();
}
